import { LitElement, css, html } from 'lit';
import { sharedStyles } from '../styles/shared.js';

/**
 * Przycisk.
 *
 * @prop {'primary'|'secondary'|'ghost'|'danger'} variant
 * @prop {'sm'|'md'} size
 * @prop {boolean} block — rozciąga na całą szerokość
 * @prop {boolean} busy — blokuje i pokazuje stan pracy
 * @prop {'button'|'submit'} type
 */
export class QzButton extends LitElement {
  static properties = {
    variant: { type: String, reflect: true },
    size: { type: String, reflect: true },
    block: { type: Boolean, reflect: true },
    busy: { type: Boolean, reflect: true },
    disabled: { type: Boolean, reflect: true },
    type: { type: String },
  };

  static styles = [
    sharedStyles,
    css`
      :host {
        display: inline-flex;
      }

      :host([block]) {
        display: flex;
        width: 100%;
      }

      button {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: var(--qz-space-2);
        width: 100%;
        padding: 0.6rem 1.1rem;
        border: 1px solid transparent;
        border-radius: var(--qz-radius-sm);
        font-family: var(--qz-font-sans);
        font-size: var(--qz-text-sm);
        font-weight: 600;
        line-height: 1.2;
        position: relative;
        cursor: pointer;
        transition:
          background var(--qz-transition),
          border-color var(--qz-transition),
          transform var(--qz-transition);
      }

      /*
       * Wciśnięcie skalą, nie przesunięciem w dół: przy palcu zasłaniającym
       * przycisk ruch w dół jest niewidoczny, a skurcz widać na całej
       * krawędzi. Na telefonie nie ma najazdu myszą, więc to jedyna
       * informacja, że dotknięcie doszło.
       */
      button:active:not(:disabled) {
        transform: scale(0.97);
        transition-duration: var(--qz-motion-tap);
      }

      button:disabled {
        cursor: not-allowed;
        opacity: 0.55;
      }

      :host([size='sm']) button {
        padding: 0.35rem 0.7rem;
        font-size: var(--qz-text-xs);
      }

      /* domyślny i jawny wariant główny */
      button,
      :host([variant='primary']) button {
        background: var(--qz-accent);
        color: var(--qz-accent-ink);
      }

      button:hover:not(:disabled),
      :host([variant='primary']) button:hover:not(:disabled) {
        background: color-mix(in oklab, var(--qz-accent) 86%, var(--qz-ink));
      }

      :host([variant='secondary']) button {
        background: var(--qz-surface);
        color: var(--qz-ink);
        border-color: var(--qz-line-strong);
      }

      :host([variant='secondary']) button:hover:not(:disabled) {
        background: var(--qz-surface-2);
      }

      :host([variant='ghost']) button {
        background: transparent;
        color: var(--qz-ink-muted);
      }

      :host([variant='ghost']) button:hover:not(:disabled) {
        background: var(--qz-surface-2);
        color: var(--qz-ink);
      }

      :host([variant='danger']) button {
        background: transparent;
        color: var(--qz-danger);
        border-color: var(--qz-danger);
      }

      :host([variant='danger']) button:hover:not(:disabled) {
        background: var(--qz-danger-soft);
      }

      /*
       * Wskaźnik pracy leży NA przycisku, a nie obok napisu. Dokładany obok
       * poszerzał przycisk w chwili kliknięcia — a wtedy przesuwa się cały
       * wiersz, w który użytkownik właśnie celuje palcem.
       */
      .spinner {
        position: absolute;
        top: 50%;
        left: 50%;
        width: 0.9em;
        height: 0.9em;
        margin: -0.45em 0 0 -0.45em;
        border: 2px solid currentColor;
        border-right-color: transparent;
        border-radius: 50%;
        animation: spin 700ms linear infinite;
      }

      .content {
        display: inline-flex;
        align-items: center;
        gap: var(--qz-space-2);
        transition: opacity var(--qz-motion-tap) var(--qz-ease);
      }

      button.busy .content {
        opacity: 0;
      }

      @keyframes spin {
        to {
          transform: rotate(360deg);
        }
      }
    `,
  ];

  constructor() {
    super();
    this.variant = 'primary';
    this.size = 'md';
    this.block = false;
    this.busy = false;
    this.disabled = false;
    this.type = 'button';
  }

  render() {
    return html`
      <button
        type="button"
        class=${this.busy ? 'busy' : ''}
        ?disabled=${this.disabled || this.busy}
        aria-busy=${this.busy ? 'true' : 'false'}
        @click=${this.#onClick}
      >
        ${this.busy ? html`<span class="spinner" aria-hidden="true"></span>` : null}
        <span class="content"><slot></slot></span>
      </button>
    `;
  }

  /**
   * Natywny przycisk w shadow DOM nie wyśle formularza leżącego na zewnątrz,
   * więc przy type="submit" szukamy formularza w drzewie światła i prosimy go
   * o wysłanie. Dzięki temu działa też Enter w polu tekstowym.
   */
  #onClick() {
    if (this.type !== 'submit') return;
    this.closest('form')?.requestSubmit();
  }
}

customElements.define('qz-button', QzButton);
